/* C4 — Campanhas (Painel do Cliente).
 *
 * A empresa configura e lê o desempenho das jornadas de oração. Variante clara
 * do Nebula. Ligada aos endpoints reais: `/api/journeys` (lista + salvar),
 * `/steps` (conteúdo por dia) e `/funnel` (funil).
 *
 * REAL hoje: configuração da campanha · cadência (tentativas antes de silenciar
 *   = max_sem_resposta) · conteúdo por dia · funil.
 * FASE 5 (marcado): cadência de reengajamento de quem não respondeu. */

import React, { useEffect, useMemo, useState } from 'react';

import { api } from '../api/cliente';
import { telefoneLegivel } from '../admin/formato';
import { Aviso, Button, card, Cabecalho, Icon, Nota, Secao, Select, Tag } from '../admin/ui';
import { useRecurso } from '../estado/recurso';
import { useSessao } from '../estado/sessao';

const inputStyle = {
  height: 40, padding: '0 12px', width: '100%',
  background: 'var(--surface-raised)', border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-control)', color: 'var(--text-primary)',
  font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', outline: 'none',
};
const h4 = { margin: 0, font: 'var(--type-label)', fontWeight: 'var(--fw-semibold)', color: 'var(--text-muted)', letterSpacing: 'var(--ls-caps)', textTransform: 'uppercase' };
const legenda = { font: 'var(--type-caption)', color: 'var(--text-muted)' };

function Campo({ label, dica, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ font: 'var(--type-label)', color: 'var(--text-secondary)' }}>{label}</span>
      {children}
      {dica && <span style={legenda}>{dica}</span>}
    </label>
  );
}

// A mesma regra do backend (`web/api/campanhas.py::ID_VALIDO`): o id entra em
// URLs e em nomes de asset, então é identificador, não texto.
const ID_VALIDO = /^[A-Za-z0-9][A-Za-z0-9_-]{1,60}$/;

// A mesma regra de `web/hub_dominio.py::PREFIXO_VALIDO`: o prefixo vira host
// (`<prefixo>.<base>`), então é rótulo DNS — minúsculas, dígitos e hífen.
const PREFIXO_VALIDO = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

/** O link de divulgação. Com domínio base configurado na plataforma E um
 *  prefixo na campanha, é `https://<prefixo>.<base>/ir/<slug>`; senão, o host
 *  do próprio painel — o `/ir/` responde igual nos dois. */
function linkDeDivulgacao(campanha, hub, prefixo = campanha.hub_prefixo) {
  const slug = campanha.slug || campanha.journey_id;
  const base = hub?.dominio_base;
  const origem = base && prefixo ? `https://${prefixo}.${base}` : window.location.origin;
  return `${origem}/ir/${slug}`;
}

/** "Oração pela Saúde" → "oracao-pela-saude". Só sugestão: a pessoa edita. */
function slugDe(nome) {
  return String(nome || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 61);
}

export default function Campanhas() {
  const recurso = useRecurso((sinal) => api.campanhas(sinal), []);
  const campanhas = useMemo(() => recurso.dados?.itens || [], [recurso.dados]);
  const [selId, setSelId] = useState(null);
  const [criando, setCriando] = useState(false);
  const selecionada = campanhas.find((c) => c.journey_id === selId) || null;

  useEffect(() => {
    if (!selId && campanhas.length) setSelId(campanhas[0].journey_id);
  }, [campanhas, selId]);

  function criada(journeyId) {
    setCriando(false);
    setSelId(journeyId);
    recurso.recarregar();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Cabecalho
        titulo="Campanhas"
        descricao="Configure a jornada de oração e acompanhe o desempenho de cada campanha."
        acoes={<Button variant="primary" icon="plus" onClick={() => setCriando(true)}>Nova campanha</Button>}
      />

      {criando && <NovaCampanha aoFechar={() => setCriando(false)} aoCriar={criada} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,320px) minmax(0,1fr)', gap: 20, flex: 1, minHeight: 0, padding: '0 24px 24px' }}>
        <Secao titulo={recurso.carregando ? 'Carregando…' : `${campanhas.length} ${campanhas.length === 1 ? 'campanha' : 'campanhas'}`} rolar>
          {recurso.erro ? (
            <Aviso titulo="As campanhas não carregaram" texto={recurso.erro.mensagem} acao={recurso.erro.temporario ? 'Tentar de novo' : null} aoAcao={recurso.recarregar} />
          ) : !recurso.carregando && campanhas.length === 0 ? (
            <Aviso titulo="Nenhuma campanha" texto="Crie a primeira campanha de oração." />
          ) : (
            <div>
              {campanhas.map((c) => {
                const ativa = c.journey_id === selId;
                return (
                  <button
                    key={c.journey_id}
                    type="button"
                    onClick={() => setSelId(c.journey_id)}
                    style={{
                      display: 'flex', flexDirection: 'column', gap: 6, width: '100%', textAlign: 'left', cursor: 'pointer',
                      padding: '14px 20px', border: 0, borderBottom: '1px solid var(--border-subtle)',
                      background: ativa ? 'var(--surface-active)' : 'transparent',
                      boxShadow: ativa ? 'inset 3px 0 0 var(--accent)' : 'none',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ font: 'var(--type-body-strong)', color: 'var(--text-primary)' }}>{c.nome}</span>
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        {c.intervalo_de_teste_minutos != null && <Tag tone="warning">teste · {c.intervalo_de_teste_minutos} min</Tag>}
                        <Tag tone={c.ativo ? 'success' : 'neutral'} dot={c.ativo}>{c.ativo ? 'Ativa' : 'Pausada'}</Tag>
                      </span>
                    </span>
                    <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{c.inscricoes_ativas ?? 0} em acompanhamento · {c.dias_total} dias</span>
                  </button>
                );
              })}
            </div>
          )}
        </Secao>

        {selecionada ? (
          <Detalhe key={selecionada.journey_id} campanha={selecionada} hub={recurso.dados?.hub} aoSalvar={recurso.recarregar} />
        ) : (
          <section style={{ ...card, alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ ...legenda }}>Selecione uma campanha.</span>
          </section>
        )}
      </div>
    </div>
  );
}

/** Modal de criação. Nasceu de um hotfix em produção (16/09): o botão "Nova
 *  campanha" estava sem `onClick` e o clique nem chegava ao backend.
 *
 *  O identificador (que vira o slug do link `/ir/<id>`) é sugerido a partir do
 *  nome e editável — a regra é a mesma do backend, conferida aqui antes do
 *  POST para o erro aparecer no campo e não num 400 genérico. O `sender` é a
 *  empresa da sessão (o cliente enxerga só a própria). Ao criar, a lista
 *  recarrega e a campanha nova fica selecionada; o resto (mensagem do link,
 *  agente, celulares) se configura no detalhe. */
function NovaCampanha({ aoFechar, aoCriar }) {
  const { tenants } = useSessao();
  const empresa = tenants?.[0]?.sender || null;
  const [nome, setNome] = useState('');
  const [id, setId] = useState('');
  const [idEditado, setIdEditado] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);

  const idFinal = (idEditado ? id : slugDe(nome)).trim();
  const idOk = ID_VALIDO.test(idFinal);
  const pronto = nome.trim().length > 0 && idOk && Boolean(empresa) && !salvando;

  async function criar(evento) {
    evento?.preventDefault();
    if (!pronto) return;
    setSalvando(true);
    setErro(null);
    try {
      const r = await api.criarCampanha({ journey_id: idFinal, nome: nome.trim(), sender: empresa });
      aoCriar(r?.journey_id || idFinal);
    } catch (e) {
      setErro(e?.codigo === 'ja_cadastrada'
        ? 'Já existe uma campanha com este identificador. Troque o identificador.'
        : (e?.mensagem || 'Não foi possível criar a campanha.'));
      setSalvando(false);
    }
  }

  return (
    <div style={{ position: 'absolute', inset: 0, background: 'var(--overlay-scrim, rgba(4,5,20,.5))', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 5 }}>
      <div style={{ ...card, width: 460, maxWidth: '100%', boxShadow: 'var(--shadow-md)' }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
          <h3 style={{ margin: 0, font: 'var(--type-h3)' }}>Nova campanha</h3>
          <button type="button" onClick={aoFechar} aria-label="Fechar" style={{ display: 'inline-flex', width: 32, height: 32, alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 0, color: 'var(--text-secondary)', cursor: 'pointer' }}><Icon name="x" size={18} /></button>
        </header>

        {/* `<form>`: Enter em qualquer campo cria, sem capturar tecla à mão. */}
        <form onSubmit={criar} style={{ display: 'contents' }}>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Campo label="Nome da campanha" dica="Como ela aparece no painel. Ex.: Oração pela Saúde.">
            <input autoFocus style={inputStyle} value={nome} onChange={(e) => { setNome(e.target.value); setErro(null); }} placeholder="Oração pela Saúde" />
          </Campo>
          <Campo label="Identificador (vai no link de divulgação)" dica={idFinal && !idOk ? 'Só letras, números, "-" e "_", de 2 a 61 caracteres, começando por letra ou número.' : `O link será ${window.location.origin}/ir/${idFinal || '…'}`}>
            <input style={{ ...inputStyle, fontFamily: 'var(--font-mono)', borderColor: idFinal && !idOk ? 'var(--danger)' : undefined }} value={idFinal} onChange={(e) => { setId(e.target.value); setIdEditado(true); setErro(null); }} placeholder="oracao-pela-saude" />
          </Campo>
          {!empresa && <span style={{ font: 'var(--type-caption)', color: 'var(--danger-text, var(--danger))' }}>Sua sessão não tem uma empresa vinculada — peça ao administrador.</span>}
          {erro && <span style={{ font: 'var(--type-caption)', color: 'var(--danger-text, var(--danger))' }}>{erro}</span>}
          <span style={legenda}>Duração, mensagem do link, agente e celulares se configuram depois, no detalhe da campanha.</span>
        </div>

        <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-sunken)' }}>
          <Button variant="ghost" onClick={aoFechar} disabled={salvando}>Cancelar</Button>
          <Button variant="primary" icon="plus" type="submit" disabled={!pronto}>{salvando ? 'Criando…' : 'Criar campanha'}</Button>
        </footer>
        </form>
      </div>
    </div>
  );
}

function Detalhe({ campanha, hub, aoSalvar }) {
  const funilRec = useRecurso((sinal) => api.funilDaCampanha(campanha.journey_id, sinal), [campanha.journey_id]);

  return (
    <div style={{ ...card, overflow: 'auto', padding: 0 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22, padding: '20px 22px 26px' }}>
        <Configuracao campanha={campanha} aoSalvar={aoSalvar} />
        <AgenteEPublico campanha={campanha} hub={hub} aoSalvar={aoSalvar} />
        <ConteudoPorFase campanha={campanha} />
        <Funil funil={funilRec.dados} carregando={funilRec.carregando} />
      </div>
    </div>
  );
}

function Configuracao({ campanha, aoSalvar }) {
  // A mensagem do link (`frase_de_entrada`) NÃO mora neste formulário: ela é
  // editada na seção "Link de divulgação", com o próprio salvar. Se estivesse
  // aqui também, este salvar mandaria a cópia velha por cima da nova.
  const [form, setForm] = useState({
    dias_total: campanha.dias_total ?? 7,
    janela_inicio: campanha.janela_inicio || '07:00',
    janela_fim: campanha.janela_fim || '21:30',
    hora_politica: campanha.hora_politica || 'primeiro_contato',
    max_sem_resposta: campanha.max_sem_resposta ?? 3,
    captacao_tentativas: campanha.captacao_tentativas ?? 0,
    politica_silencio: campanha.politica_silencio || 'encerrar_no_limite',
  });
  // Os dias de seguimento moram como texto no formulário ("7, 15, 30") e só
  // viram lista de números no salvar. Editar dígito a dígito uma lista é hostil.
  const [offsetsTexto, setOffsetsTexto] = useState((campanha.seguimento_offsets || []).join(', '));
  // Modo de teste (0016): ligado, cada toque acontece N minutos depois do
  // anterior, ignorando a janela — a jornada de 7 dias roda em menos de uma
  // hora. É um interruptor à parte, e não só um número, porque o perigo é o
  // esquecimento: ligado numa campanha real, manda oração a cada N minutos.
  const [modoTeste, setModoTeste] = useState(campanha.intervalo_de_teste_minutos != null);
  const [intervaloTeste, setIntervaloTeste] = useState(campanha.intervalo_de_teste_minutos ?? 5);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const set = (campo) => (valor) => { setForm((f) => ({ ...f, [campo]: valor })); setSalvo(false); };
  const editarOffsets = (valor) => { setOffsetsTexto(valor); setSalvo(false); };

  // "Seguir até o fim" ignora o limite de silêncio: o toque diário vai até o
  // último dia da janela. O campo de limite fica visível, mas apagado.
  const seguirAteOFim = form.politica_silencio === 'seguir_ate_o_fim';

  async function salvar() {
    setSalvando(true);
    try {
      const offsets = offsetsTexto
        .split(/[\s,;]+/)
        .map((n) => parseInt(n, 10))
        .filter((n) => Number.isInteger(n) && n > 0);
      await api.salvarCampanha(campanha.journey_id, {
        ...form,
        seguimento_offsets: offsets,
        // `null` desliga: o backend trata limpar como tão importante quanto ligar.
        intervalo_de_teste_minutos: modoTeste ? Number(intervaloTeste) || 5 : null,
      });
      setSalvo(true);
      aoSalvar?.();
      setTimeout(() => setSalvo(false), 2200);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h4 style={h4}>Configuração</h4>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
        <Campo label="Duração (dias)">
          <input type="number" min="1" style={inputStyle} value={form.dias_total} onChange={(e) => set('dias_total')(Number(e.target.value))} />
        </Campo>
        <Campo label="Janela — início">
          <input type="time" style={inputStyle} value={form.janela_inicio} onChange={(e) => set('janela_inicio')(e.target.value)} />
        </Campo>
        <Campo label="Janela — fim">
          <input type="time" style={inputStyle} value={form.janela_fim} onChange={(e) => set('janela_fim')(e.target.value)} />
        </Campo>
      </div>
      <Campo label="Horário de envio" dica="O horário dinâmico repete o horário do primeiro contato da pessoa.">
        <Select value={form.hora_politica} onChange={(e) => set('hora_politica')(e.target.value)} fullWidth options={[
          { value: 'primeiro_contato', label: 'Repetir o horário do primeiro contato' },
          { value: 'fixa', label: 'Horário fixo' },
        ]} />
      </Campo>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 14, background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-control)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, ...h4 }}><Icon name="clock" size={13} /> Cadência e reengajamento</span>

        <Campo label="Tentativas de captação do pedido" dica="É o que liga a automação já na PRIMEIRA mensagem, sem depender de resposta: quem chega e some recebe até N toques leves pedindo o pedido de oração (no modo de teste, N minutos depois; em produção, no dia seguinte). 0 desliga — quem não responder não recebe nada.">
          <input type="number" min="0" max="10" style={{ ...inputStyle, maxWidth: 120 }} value={form.captacao_tentativas} onChange={(e) => set('captacao_tentativas')(Number(e.target.value))} />
        </Campo>

        <Campo label="Quando a pessoa não responde" dica="'Seguir até o fim' mantém um toque por dia durante toda a janela, mesmo nos dias sem resposta — é o modelo de reengajamento. 'Encerrar no limite' se despede após as tentativas abaixo.">
          <Select value={form.politica_silencio} onChange={(e) => set('politica_silencio')(e.target.value)} fullWidth options={[
            { value: 'encerrar_no_limite', label: 'Encerrar após as tentativas (padrão)' },
            { value: 'seguir_ate_o_fim', label: 'Seguir até o fim da janela' },
          ]} />
        </Campo>

        <Campo label="Tentativas antes de silenciar" dica={seguirAteOFim ? 'Ignorado enquanto a política é "Seguir até o fim da janela".' : 'Após esse número de toques seguidos sem resposta, a Sofia se despede e para de enviar.'}>
          <input type="number" min="1" disabled={seguirAteOFim} style={{ ...inputStyle, maxWidth: 120, opacity: seguirAteOFim ? 0.5 : 1 }} value={form.max_sem_resposta} onChange={(e) => set('max_sem_resposta')(Number(e.target.value))} />
        </Campo>

        <Campo label="Dias de seguimento" dica="Reaproximações depois do fim da janela, em dias corridos (ex.: 7, 15, 30). Vazio desliga o seguimento.">
          <input style={inputStyle} inputMode="numeric" placeholder="ex.: 7, 15, 30" value={offsetsTexto} onChange={(e) => editarOffsets(e.target.value)} />
        </Campo>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14, background: modoTeste ? 'var(--warning-soft)' : 'var(--surface-sunken)', border: `1px solid ${modoTeste ? 'var(--warning)' : 'var(--border-subtle)'}`, borderRadius: 'var(--radius-control)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={modoTeste} onChange={(e) => { setModoTeste(e.target.checked); setSalvo(false); }} />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, ...h4, color: modoTeste ? 'var(--warning-text)' : h4.color }}><Icon name="clock" size={13} /> Modo de teste (debug)</span>
          {modoTeste && <Tag tone="warning">ligado</Tag>}
        </label>
        {modoTeste && (
          <Campo label="Intervalo entre os toques (minutos)" dica="Cada toque acontece N minutos depois do anterior, ignorando a janela de envio — a jornada inteira roda em menos de uma hora. Todo o resto (guardas, silêncio, reativação, funil) é o mesmo de produção.">
            <input type="number" min="1" max="1440" style={{ ...inputStyle, maxWidth: 120, background: 'var(--surface-raised)' }} value={intervaloTeste} onChange={(e) => { setIntervaloTeste(e.target.value); setSalvo(false); }} />
          </Campo>
        )}
        <span style={{ ...legenda, color: modoTeste ? 'var(--warning-text)' : legenda.color }}>
          {modoTeste
            ? 'DESLIGUE antes de ligar o anúncio: esquecido, uma campanha real manda oração a cada poucos minutos.'
            : 'Desligado = produção (um toque por dia, dentro da janela). Ligue só para validar o fluxo.'}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Button variant="primary" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar configuração'}</Button>
        {salvo && <span style={{ font: 'var(--type-caption)', color: 'var(--success-text)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="check" size={13} /> Salvo</span>}
      </div>
    </section>
  );
}

// As três fases de conteúdo, cada uma com quantos "slots" tem (derivado da
// config da campanha), como rotula cada item e quais campos mostra. A captação
// não ora — o motor só lê a orientação de cada tentativa —, então ali o roteiro
// nem aparece. No seguimento o rótulo é o offset em dias, não "Dia N".
const FASES = [
  {
    id: 'janela', rotulo: 'Jornada',
    slots: (c) => Math.max(0, c.dias_total ?? 7),
    rotuloItem: (dia) => `Dia ${dia}`,
    temRoteiro: true,
    phRoteiro: 'Base da oração deste dia',
    phOrientacao: 'Orientação de assunto do dia',
    nota: 'A base é adaptada por pessoa, não recitada.',
    vazio: null,
  },
  {
    id: 'captacao', rotulo: 'Captação',
    slots: (c) => Math.max(0, c.captacao_tentativas ?? 0),
    rotuloItem: (dia) => `Tentativa ${dia}`,
    temRoteiro: false,
    phOrientacao: 'Assunto desta tentativa (ex.: reabrir com carinho e pedir o pedido)',
    nota: 'A Sofia só reabre a conversa e pede o pedido — não ora na captação.',
    vazio: 'Captação desligada. Ligue em "Tentativas de captação do pedido", na configuração acima.',
  },
  {
    id: 'seguimento', rotulo: 'Seguimento',
    slots: (c) => (c.seguimento_offsets || []).length,
    rotuloItem: (dia, c) => `${(c.seguimento_offsets || [])[dia - 1]} dias`,
    temRoteiro: true,
    phRoteiro: 'Base da reaproximação deste toque',
    phOrientacao: 'Orientação deste seguimento',
    nota: 'Reaproximação depois do fim da janela — sem oração diária.',
    vazio: 'Sem seguimento. Defina os "Dias de seguimento", na configuração acima.',
  },
];

function ConteudoPorFase({ campanha }) {
  const [faseId, setFaseId] = useState('janela');
  const fase = FASES.find((f) => f.id === faseId);
  const rec = useRecurso(
    (sinal) => api.passosDaCampanha(campanha.journey_id, faseId, sinal),
    [campanha.journey_id, faseId],
  );

  // Os slots vêm da config (dias_total / tentativas / offsets); o conteúdo
  // existente é encaixado neles. Assim os dias aparecem editáveis mesmo antes
  // de ter texto, e mudar a config acima muda a quantidade de campos aqui.
  const total = fase.slots(campanha);
  const [dias, setDias] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    if (!rec.dados) return;
    const porDia = new Map((rec.dados.itens || []).map((p) => [p.dia, p]));
    setDias(Array.from({ length: total }, (_, i) => {
      const ex = porDia.get(i + 1) || {};
      return { dia: i + 1, roteiro: ex.roteiro || '', orientacao: ex.orientacao || '' };
    }));
  }, [rec.dados, total]);

  const set = (i, campo) => (valor) => { setDias((d) => d.map((p, j) => (j === i ? { ...p, [campo]: valor } : p))); setSalvo(false); };

  async function salvar() {
    setSalvando(true);
    try {
      await api.salvarPassosDaCampanha(campanha.journey_id, dias, faseId);
      setSalvo(true);
      setTimeout(() => setSalvo(false), 2200);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14, borderTop: '1px solid var(--border-subtle)', paddingTop: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h4 style={h4}>Conteúdo por fase</h4>
        <span style={legenda}>{fase.nota}</span>
      </div>

      <div role="tablist" aria-label="Fase do conteúdo" style={{ display: 'flex', gap: 4, padding: 3, background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-control)', width: 'fit-content' }}>
        {FASES.map((f) => {
          const ativa = f.id === faseId;
          return (
            <button
              key={f.id} type="button" role="tab" aria-selected={ativa}
              onClick={() => setFaseId(f.id)}
              style={{
                cursor: 'pointer', border: 0, borderRadius: 'var(--radius-sm, 6px)',
                padding: '6px 14px', font: 'var(--type-label)',
                color: ativa ? 'var(--text-brand)' : 'var(--text-secondary)',
                background: ativa ? 'var(--surface-raised)' : 'transparent',
                boxShadow: ativa ? 'var(--elevation-1, 0 1px 2px rgba(0,0,0,.08))' : 'none',
              }}
            >{f.rotulo}</button>
          );
        })}
      </div>

      {rec.carregando && !dias ? (
        <span style={legenda}>Carregando…</span>
      ) : total === 0 ? (
        <span style={legenda}>{fase.vazio}</span>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(dias || []).map((p, i) => (
              <div key={p.dia} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 14, background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-control)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 26, height: 22, padding: '0 8px', borderRadius: 'var(--radius-pill)', background: 'var(--accent-soft)', color: 'var(--text-brand)', font: 'var(--type-caption)', fontWeight: 'var(--fw-semibold)' }}>{fase.rotuloItem(p.dia, campanha)}</span>
                  {fase.temRoteiro && !p.roteiro && <span style={{ font: 'var(--type-caption)', color: 'var(--text-placeholder)' }}>sem base — a Sofia improvisa no tema</span>}
                </div>
                {fase.temRoteiro && (
                  <textarea rows={2} value={p.roteiro} onChange={(e) => set(i, 'roteiro')(e.target.value)} placeholder={fase.phRoteiro} style={{ ...inputStyle, height: 'auto', padding: 10, lineHeight: 'var(--lh-relaxed)', resize: 'vertical' }} />
                )}
                <input value={p.orientacao} onChange={(e) => set(i, 'orientacao')(e.target.value)} placeholder={fase.phOrientacao} style={inputStyle} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button variant="secondary" onClick={salvar} disabled={salvando || !dias}>{salvando ? 'Salvando…' : 'Salvar conteúdo'}</Button>
            {salvo && <span style={{ font: 'var(--type-caption)', color: 'var(--success-text)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="check" size={13} /> Salvo</span>}
          </div>
        </>
      )}
    </section>
  );
}

function Funil({ funil, carregando }) {
  const estagios = useMemo(() => {
    if (!funil) return [];
    const e = funil.eventos || {};
    const base = funil.chegadas || 1;
    return [
      { rotulo: 'Chegaram', valor: funil.chegadas || 0, tom: 'var(--chart-2, #8878FD)' },
      { rotulo: 'Inscreveram', valor: e.inscrita || 0, tom: 'var(--accent)' },
      { rotulo: 'Concluíram', valor: e.concluida || 0, tom: 'var(--success)' },
      { rotulo: 'Recusaram', valor: e.recusada || 0, tom: 'var(--danger)' },
    ].map((s) => ({ ...s, pct: Math.round((s.valor / base) * 100) }));
  }, [funil]);

  const secundarios = funil ? [
    ['Silenciadas', funil.eventos?.silenciada],
    ['Reativadas', funil.eventos?.reativada],
    ['Interesse no estudo', funil.eventos?.interesse_registrado],
  ] : [];

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14, borderTop: '1px solid var(--border-subtle)', paddingTop: 22 }}>
      <h4 style={h4}>Funil</h4>
      {carregando && !funil ? (
        <span style={legenda}>Carregando o funil…</span>
      ) : !funil ? (
        <span style={legenda}>Sem dados de funil ainda.</span>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {estagios.map((s) => (
              <div key={s.rotulo} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 96, flex: 'none', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)' }}>{s.rotulo}</span>
                <span style={{ flex: 1, height: 26, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm, 6px)', overflow: 'hidden', position: 'relative' }}>
                  <span style={{ position: 'absolute', inset: 0, width: `${Math.max(s.pct, 2)}%`, background: s.tom, opacity: 0.85, borderRadius: 'inherit' }} />
                </span>
                <span className="nb-tab" style={{ width: 84, flex: 'none', textAlign: 'right', font: 'var(--type-body-strong)', fontSize: 'var(--fs-body-sm)' }}>{s.valor} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· {s.pct}%</span></span>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            {secundarios.map(([rotulo, valor]) => (
              <div key={rotulo} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '12px 14px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-control)' }}>
                <span className="nb-tab" style={{ font: 'var(--fw-bold) 20px/1.1 var(--font-display)' }}>{valor ?? 0}</span>
                <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{rotulo}</span>
              </div>
            ))}
          </div>
          <Nota>Respostas por dia caem no dia 3 — é onde a cadência mais perde gente. Fonte: /api/journeys/…/funnel.</Nota>
        </>
      )}
    </section>
  );
}

/** O estado do endereço próprio, e — só quando falta — o que fazer no DNS.
 *
 *  A tela confere sozinha ao abrir. Numa instância provisionada com curinga
 *  (o padrão: `*.<instância>` no DNS e no certificado), salvar o endereço já
 *  basta, e o cliente nunca vê instrução nenhuma. Quando o endereço está num
 *  domínio que não é nosso, a verificação falha no primeiro degrau — e é aí,
 *  e só aí, que aparece o registro a criar.
 *
 *  Nada disso é configurado: o que decide é o resultado da verificação. */
function EstadoDoEndereco({ host, destino, verificacao, verificando, aoVerificar }) {
  const caixa = { display: 'flex', flexDirection: 'column', gap: 10, padding: 12, background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-control)' };
  const celula = { padding: '6px 10px', borderBottom: '1px solid var(--border-subtle)', font: 'var(--type-caption)' };
  const mono = { fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' };

  const falha = verificacao?.etapas?.find((e) => !e.ok) || null;
  const faltaDns = falha?.nome === 'dns';
  const botao = (
    <Button variant="secondary" onClick={aoVerificar} disabled={verificando} style={{ height: 34 }}>
      {verificando ? 'Verificando…' : (verificacao ? 'Verificar de novo' : 'Verificar configuração')}
    </Button>
  );

  // Primeira conferência em andamento: uma linha, sem assustar com instrução
  // que talvez nem seja necessária.
  if (verificando && !verificacao) {
    return <span style={legenda}>Conferindo o endereço {host}…</span>;
  }

  if (verificacao?.ok) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ font: 'var(--type-caption)', color: 'var(--success-text)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Icon name="check" size={13} /> {verificacao.etapas[verificacao.etapas.length - 1]?.detalhe || 'O endereço está funcionando.'}
        </span>
        {botao}
      </div>
    );
  }

  return (
    <div style={caixa}>
      {faltaDns ? (
        <>
          <span style={{ font: 'var(--type-label)', color: 'var(--text-secondary)' }}>Falta criar o endereço no seu DNS</span>
          <span style={legenda}>No provedor do domínio (no Cloudflare: <b>DNS → Registros → Adicionar registro</b>), preencha assim e salve:</span>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              <tr><td style={{ ...celula, color: 'var(--text-muted)', width: 90 }}>Tipo</td><td style={{ ...celula, ...mono }}>CNAME</td></tr>
              <tr><td style={{ ...celula, color: 'var(--text-muted)' }}>Nome</td><td style={{ ...celula, ...mono }}>{host.split('.')[0]}</td></tr>
              <tr><td style={{ ...celula, color: 'var(--text-muted)', borderBottom: 0 }}>Destino</td><td style={{ ...celula, borderBottom: 0, ...mono }}>{destino}</td></tr>
            </tbody>
          </table>
        </>
      ) : (
        <span style={{ font: 'var(--type-label)', color: 'var(--text-secondary)' }}>O endereço ainda não está pronto</span>
      )}
      {falha && (
        <span style={{ display: 'flex', gap: 8, alignItems: 'flex-start', font: 'var(--type-caption)', color: 'var(--danger-text, var(--danger))' }}>
          <Icon name="x" size={13} style={{ flex: 'none', marginTop: 2 }} />
          <span>{falha.detalhe}</span>
        </span>
      )}
      <div>{botao}</div>
    </div>
  );
}

/** Agente, link de divulgação e celulares da campanha.
 *
 *  O link (`/ir/<slug>`) é só um redirecionador: no clique, o servidor escolhe
 *  um celular conectado entre os ticados abaixo (sem nenhum ticado, qualquer
 *  celular conectado da empresa) e responde 302 para
 *  `https://wa.me/<celular>?text=<mensagem>`. A mensagem é a
 *  `frase_de_entrada` da campanha — o mesmo texto que, ao chegar pelo WhatsApp,
 *  identifica de qual campanha a pessoa veio. Por isso ela é editada AQUI, ao
 *  lado do link, e não na configuração geral.
 *
 *  O "endereço do link" (`hub_prefixo`) troca o host que aparece no anúncio:
 *  `https://atendimento.<base>/ir/<slug>`. O `<base>` é o domínio do próprio
 *  painel sem o primeiro rótulo (`hub.dominio_base`, que vem com a listagem);
 *  sem ele (painel em `localhost`) o campo nem aparece. Salvo o endereço, a
 *  tela mostra o único passo fora daqui — um CNAME no Cloudflare apontando
 *  para o host do painel — e o botão "Verificar" testa DNS → HTTPS →
 *  redirecionamento e explica o que falta. */
function AgenteEPublico({ campanha, hub, aoSalvar }) {
  const agentesRec = useRecurso((sinal) => api.agentes(sinal), []);
  const numerosRec = useRecurso((sinal) => api.numeros(sinal), []);
  const agentes = agentesRec.dados?.itens || [];
  const numeros = numerosRec.dados?.itens || [];
  const [copiado, setCopiado] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [mensagem, setMensagem] = useState(campanha.frase_de_entrada || '');
  const [salvandoMensagem, setSalvandoMensagem] = useState(false);
  const [mensagemSalva, setMensagemSalva] = useState(false);
  const [erro, setErro] = useState(null);
  const [prefixo, setPrefixo] = useState(campanha.hub_prefixo || '');
  const [salvandoPrefixo, setSalvandoPrefixo] = useState(false);
  const [prefixoSalvo, setPrefixoSalvo] = useState(false);
  const [verificando, setVerificando] = useState(false);
  const [verificacao, setVerificacao] = useState(null);

  // Sem domínio base (painel em localhost) não há endereço a oferecer.
  const dominioBase = hub?.dominio_base || null;
  const prefixoLimpo = prefixo.trim().toLowerCase();
  const prefixoOk = !prefixoLimpo || PREFIXO_VALIDO.test(prefixoLimpo);
  const prefixoMudou = prefixoLimpo !== (campanha.hub_prefixo || '');

  const link = linkDeDivulgacao(campanha, hub);
  const mensagemMudou = mensagem !== (campanha.frase_de_entrada || '');

  // Prévia do que o link faz — com o primeiro celular ticado e conectado, ou
  // um marcador quando ainda não há nenhum.
  const ticados = numeros.filter((n) => n.campanha_id === campanha.journey_id);
  const exemplo = ticados.find((n) => n.conexao === 'open') || ticados[0] || null;
  const destino = `https://wa.me/${exemplo?.telefone || exemplo?.sender?.replace(/\D/g, '') || '<celular>'}${mensagem ? `?text=${encodeURIComponent(mensagem)}` : ''}`;

  async function trocarAgente(valor) {
    setOcupado(true);
    setErro(null);
    try {
      await api.salvarCampanha(campanha.journey_id, { agente_id: valor ? Number(valor) : null });
      aoSalvar?.();
    } catch (e) {
      setErro(e?.mensagem || 'Não foi possível trocar o agente.');
    } finally { setOcupado(false); }
  }

  async function salvarMensagem() {
    setSalvandoMensagem(true);
    setErro(null);
    try {
      await api.salvarCampanha(campanha.journey_id, { frase_de_entrada: mensagem.trim() || null });
      setMensagemSalva(true);
      aoSalvar?.();
      setTimeout(() => setMensagemSalva(false), 2200);
    } catch (e) {
      setErro(e?.mensagem || 'Não foi possível salvar a mensagem.');
    } finally { setSalvandoMensagem(false); }
  }

  async function salvarPrefixo() {
    if (!prefixoOk) return;
    setSalvandoPrefixo(true);
    setErro(null);
    try {
      await api.salvarCampanha(campanha.journey_id, { hub_prefixo: prefixoLimpo || null });
      setPrefixoSalvo(true);
      setVerificacao(null);   // endereço novo, verificação antiga não vale
      aoSalvar?.();
      setTimeout(() => setPrefixoSalvo(false), 2200);
    } catch (e) {
      setErro(e?.mensagem || 'Não foi possível salvar o endereço do link.');
    } finally { setSalvandoPrefixo(false); }
  }

  async function verificarEndereco() {
    setVerificando(true);
    setErro(null);
    try {
      setVerificacao(await api.verificarEnderecoDoLink(campanha.journey_id));
    } catch (e) {
      setErro(e?.mensagem || 'Não foi possível verificar o endereço.');
    } finally { setVerificando(false); }
  }

  // Confere sozinha ao abrir a campanha que já tem endereço: é o que permite
  // mostrar instrução de DNS só para quem precisa. Uma vez por campanha — o
  // `key` do Detalhe remonta este componente a cada troca.
  const enderecoSalvo = Boolean(campanha.hub_prefixo && dominioBase);
  useEffect(() => {
    if (enderecoSalvo) verificarEndereco();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enderecoSalvo]);

  async function alternarNumero(n) {
    const atribuido = n.campanha_id === campanha.journey_id;
    setOcupado(true);
    setErro(null);
    try {
      await api.salvarNumero(n.numero_id, { campanha_id: atribuido ? null : campanha.journey_id });
      numerosRec.recarregar();
      aoSalvar?.();   // a lista da esquerda mostra a contagem de celulares
    } catch (e) {
      setErro(e?.mensagem || 'Não foi possível alterar o celular.');
    } finally { setOcupado(false); }
  }

  async function copiar() {
    try { await navigator.clipboard.writeText(link); setCopiado(true); setTimeout(() => setCopiado(false), 1500); } catch { /* sem clipboard */ }
  }

  const opcoes = [{ value: '', label: 'Sem agente definido' }, ...agentes.map((a) => ({ value: String(a.agente_id), label: a.nome }))];

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14, borderTop: '1px solid var(--border-subtle)', paddingTop: 22 }}>
      <h4 style={h4}>Agente, link e celulares</h4>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ font: 'var(--type-label)', color: 'var(--text-secondary)' }}>Agente que atende esta campanha</span>
        <Select value={String(campanha.agente_id || '')} onChange={(e) => trocarAgente(e.target.value)} fullWidth options={opcoes} />
        {agentes.length === 0 && <span style={legenda}>Crie um agente na aba Agentes primeiro.</span>}
      </label>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 14, background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-control)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, ...h4 }}><Icon name="link" size={13} /> Link de divulgação</span>

        <Campo label="Mensagem que o link abre no WhatsApp" dica="Vai pré-digitada na conversa quando a pessoa clica no anúncio. É também o que identifica esta campanha quando a mensagem chega — mantenha um texto próprio dela.">
          <input style={{ ...inputStyle, background: 'var(--surface-raised)' }} value={mensagem} onChange={(e) => { setMensagem(e.target.value); setMensagemSalva(false); }} placeholder="ex.: Olá! Quero receber a jornada de oração." />
        </Campo>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button variant="secondary" onClick={salvarMensagem} disabled={salvandoMensagem || !mensagemMudou} style={{ height: 34 }}>{salvandoMensagem ? 'Salvando…' : 'Salvar mensagem'}</Button>
          {mensagemSalva && <span style={{ font: 'var(--type-caption)', color: 'var(--success-text)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="check" size={13} /> Salva</span>}
        </div>

        {dominioBase && (
          <>
            <Campo label="Endereço do link" dica={prefixoLimpo && !prefixoOk ? 'Só letras minúsculas, números e "-", sem começar ou terminar com "-", até 63 caracteres.' : `O link ficará ${linkDeDivulgacao(campanha, hub, prefixoLimpo)}. Vazio, usa o endereço do painel.`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>https://</span>
                <input style={{ ...inputStyle, background: 'var(--surface-raised)', fontFamily: 'var(--font-mono)', flex: 1, width: 'auto', borderColor: prefixoLimpo && !prefixoOk ? 'var(--danger)' : undefined }} value={prefixo} onChange={(e) => { setPrefixo(e.target.value); setPrefixoSalvo(false); setErro(null); }} placeholder="atendimento" spellCheck={false} />
                <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>.{dominioBase}</span>
              </div>
            </Campo>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Button variant="secondary" onClick={salvarPrefixo} disabled={salvandoPrefixo || !prefixoMudou || !prefixoOk} style={{ height: 34 }}>{salvandoPrefixo ? 'Salvando…' : 'Salvar endereço'}</Button>
              {prefixoSalvo && <span style={{ font: 'var(--type-caption)', color: 'var(--success-text)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="check" size={13} /> Salvo</span>}
            </div>

            {/* Vale para o endereço SALVO — fica à vista mesmo com uma edição
                pendente no campo, para não sumir no meio do caminho. */}
            {campanha.hub_prefixo && (
              <EstadoDoEndereco
                host={`${campanha.hub_prefixo}.${dominioBase}`}
                destino={hub?.host || window.location.hostname}
                verificacao={verificacao}
                verificando={verificando}
                aoVerificar={verificarEndereco}
              />
            )}
          </>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ font: 'var(--type-label)', color: 'var(--text-secondary)' }}>Link para os anúncios</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-control)' }}>
            <code style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link}</code>
            <Button variant="secondary" icon={copiado ? 'check' : 'link'} onClick={copiar} style={{ height: 32, padding: '0 10px', fontSize: 'var(--fs-label)' }}>{copiado ? 'Copiado' : 'Copiar'}</Button>
          </div>
          <span style={legenda}>
            Quem clica é redirecionado para <code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-caption, 12px)', wordBreak: 'break-all' }}>{destino}</code>
            {ticados.length > 1 ? ` — o celular é sorteado entre os ${ticados.length} ticados abaixo a cada clique.` : ticados.length === 1 ? '' : ' — sem celular ticado, o link usa qualquer celular conectado da empresa.'}
          </span>
        </div>
      </div>

      {erro && <span style={{ font: 'var(--type-caption)', color: 'var(--danger-text, var(--danger))' }}>{erro}</span>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ font: 'var(--type-label)', color: 'var(--text-secondary)' }}>Celulares que atendem esta campanha</span>
        {numerosRec.erro ? (
          <Aviso titulo="Os celulares não carregaram" texto={numerosRec.erro.mensagem} acao="Tentar de novo" aoAcao={numerosRec.recarregar} />
        ) : numerosRec.carregando && numeros.length === 0 ? (
          <span style={legenda}>Carregando…</span>
        ) : numeros.length === 0 ? (
          <span style={legenda}>Nenhum celular conectado ainda. Conecte um na aba Celulares.</span>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {numeros.map((n) => {
              const aqui = n.campanha_id === campanha.journey_id;
              const noutra = Boolean(n.campanha_id) && !aqui;
              const semCelular = !n.telefone && !n.sender;
              return (
                <label key={n.numero_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--surface-sunken)', border: `1px solid ${aqui ? 'var(--accent)' : 'var(--border-subtle)'}`, borderRadius: 'var(--radius-control)', cursor: ocupado ? 'default' : 'pointer' }}>
                  <input type="checkbox" checked={aqui} disabled={ocupado} onChange={() => alternarNumero(n)} />
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <span style={{ font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-primary)' }}>{semCelular ? n.instancia : telefoneLegivel(n.telefone || n.sender)}</span>
                    {!semCelular && <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{n.instancia}</span>}
                  </span>
                  {noutra && <Tag tone="neutral">em outra campanha</Tag>}
                  <Tag tone={n.conexao === 'open' ? 'success' : 'danger'} dot={n.conexao === 'open'}>{n.conexao === 'open' ? 'conectado' : 'offline'}</Tag>
                </label>
              );
            })}
          </div>
        )}
        <span style={legenda}>Um celular atende uma campanha por vez; marcar aqui o remaneja para esta. O link sorteia entre os marcados que estiverem conectados.</span>
      </div>
    </section>
  );
}
